import React, { FC, useState, ChangeEvent, useDeferredValue, useMemo, useContext } from 'react';
import { Box, Text, TextFieldSize, TextFieldAppearance, Combobox, FormControl } from '@bechtel/gds';
import { useField } from 'formik';
import { useLingui } from '@lingui/react';
import { CaddySelectorProps } from './types';
import { t } from '@lingui/macro';
import { useQuery } from '@apollo/client';
import { GET_CADDIES } from '@bechtel/client-gql';
import { Caddy, CaddyFilterInput, CaddyList, CaddyStatusCode, PagingInput } from '@bechtel/types';
import { filter, findIndex, get } from 'lodash';
import { RodRoomDataContext } from '~/utilities/context';

const CaddyItem = ({ item }: any) => {
  return (
    <Text size="400" color="grey.1000">
      {item?.label}
    </Text>
  );
};

const CaddySelectedItem = ({ item }: any) => {
  return (
    <Text size="400" color="grey.1000" family="graphik">
      {item?.label}
    </Text>
  );
};

export const CaddySelector: FC<CaddySelectorProps> = ({ fieldName, selectedCaddies }) => {
  const { selectedRodRoom } = useContext(RodRoomDataContext);
  const { i18n } = useLingui();
  const [searchText, setSearchText] = useState('');
  const [field, meta, helpers] = useField(fieldName);
  const { setValue } = helpers;
  const deferredSearchText = useDeferredValue(searchText);
  const itemToString = (item: Caddy) => item?.label;

  const { loading, data } = useQuery<
    { caddies: CaddyList },
    {
      input: CaddyFilterInput;
      paging: PagingInput;
    }
  >(GET_CADDIES, {
    variables: {
      input: {
        rodRoomId: selectedRodRoom?.id,
        searchKeyword: deferredSearchText,
        status: CaddyStatusCode.ONHAND,
      },
      paging: { cursor: '1', limit: 20 },
    },
    notifyOnNetworkStatusChange: true,
  });

  const caddies: Caddy[] = get(data, 'caddies.items', []).filter(
    (caddy: Caddy) => caddy.status === CaddyStatusCode.ONHAND
  );

  const items = useMemo(() => {
    return filter(caddies, (caddy) =>
      findIndex(selectedCaddies, (item) => item.id === caddy.id) === -1 ? true : false
    );
  }, [caddies, selectedCaddies]);

  return (
    <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }}>
      <FormControl label={t`Caddy No.`}>
        <Combobox
          size={TextFieldSize.LARGE}
          items={items}
          appearance={TextFieldAppearance.NORMAL}
          itemComponent={CaddyItem}
          selectedComponent={CaddySelectedItem}
          selectedItem={field.value}
          onSelect={async (selectedItem: any) => {
            setValue(selectedItem);
          }}
          term={searchText}
          setTerm={setSearchText}
          handleChange={(event: ChangeEvent<HTMLInputElement>) => {
            setSearchText(event.target.value);
          }}
          itemString={itemToString}
          restorePreviousOnSkip
          messageOnNoResults={t`No data found`}
          loading={loading}
        />
      </FormControl>
      {meta.touched && meta.error ? (
        <Box pt={{ sm: '200' }}>
          <Text color="bechtel.red">{i18n._(meta.error)}</Text>
        </Box>
      ) : null}
    </Box>
  );
};
